import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PaginatedCancelledTrialMemberReportList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseCancelledTrialMembers {
  dateStart?: string;
  dateEnd?: string;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useCancelledTrialMembers({
  dateStart,
  dateEnd,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseCancelledTrialMembers): UseInfiniteQueryResult<
  AxiosResponse<PaginatedCancelledTrialMemberReportList>
> {
  const fetchCancelledTrialMembers = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedCancelledTrialMemberReportList>> => {
      const params = pageParam;

      if (dateStart !== undefined) {
        params.start_date = dateStart;
      }
      if (dateEnd !== undefined) {
        params.end_date = dateEnd;
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`reports/cancelled-trial-members?${encodeUrlParams(params)}`);
    },
    [limit, ordering, dateStart, dateEnd],
  );

  return useInfiniteQuery(
    ['cancelled-trial-members', dateStart, dateEnd, ordering, csvExport],
    fetchCancelledTrialMembers,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled: enabled && dateStart !== undefined && dateEnd !== undefined,
      staleTime: 86400000, // 1 day
    },
  );
}

export default useCancelledTrialMembers;
